/**
 * Phase 41 Plan 41-11 (Surface 5a) — TanStack Query hooks for ProjectSlaTier CRUD.
 *
 * Per-project query key (`['projectSlaTiers', projectId]`) so switching
 * projects in SlaTiersAdminPage doesn't poison the cache, and so a tier
 * mutation only invalidates the affected project's tier list.
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  fetchProjectSlaTiers,
  createProjectSlaTier,
  updateProjectSlaTier,
  deleteProjectSlaTier,
  type CreateProjectSlaTierInput,
  type UpdateProjectSlaTierInput,
} from '../services/api/projectSlaTier';

export function useProjectSlaTiers(projectId: number | undefined) {
  return useQuery({
    queryKey: ['projectSlaTiers', projectId],
    queryFn: () => fetchProjectSlaTiers(projectId!),
    enabled: !!projectId,
  });
}

export function useCreateProjectSlaTier() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      projectId,
      input,
    }: {
      projectId: number;
      input: CreateProjectSlaTierInput;
    }) => createProjectSlaTier(projectId, input),
    onSuccess: (_d, { projectId }) =>
      qc.invalidateQueries({ queryKey: ['projectSlaTiers', projectId] }),
  });
}

export function useUpdateProjectSlaTier() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      projectId,
      id,
      input,
    }: {
      projectId: number;
      id: number;
      input: UpdateProjectSlaTierInput;
    }) => updateProjectSlaTier(projectId, id, input),
    onSuccess: (_d, { projectId }) =>
      qc.invalidateQueries({ queryKey: ['projectSlaTiers', projectId] }),
  });
}

export function useDeleteProjectSlaTier() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ projectId, id }: { projectId: number; id: number }) =>
      deleteProjectSlaTier(projectId, id),
    onSuccess: (_d, { projectId }) =>
      qc.invalidateQueries({ queryKey: ['projectSlaTiers', projectId] }),
  });
}
